import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import Pessoa, { Vinculo, VinculoNome } from '../../../model/Pessoa';

interface PessoaEditModalProps {
    show: boolean;
    handleClose: () => void;
    pessoa: Pessoa | null;
    onUpdate: (pessoa: Pessoa) => void;
    vinculos: Vinculo[];
}

const PessoaEditModal: React.FC<PessoaEditModalProps> = ({ show, handleClose, pessoa, onUpdate, vinculos }) => {
    const [editedPessoa, setEditedPessoa] = useState<Pessoa | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (pessoa) {
            setEditedPessoa({
                ...pessoa,
                telefone: pessoa.telefone || '' // Inicializa o telefone
            });
        }
    }, [pessoa]);

    const handleChange = (e: React.ChangeEvent<any>) => {
        const { name, value } = e.target;

        if (e.target.tagName === 'SELECT') {
            const selectedVinculo = vinculos.find(v => v.nome === value);
            setEditedPessoa(prevState => prevState ? ({
                ...prevState,
                vinculo: selectedVinculo ? { id: selectedVinculo.id, nome: selectedVinculo.nome } : prevState.vinculo
            }) : null);
        } else {
            setEditedPessoa(prevState => prevState ? ({
                ...prevState,
                [name]: value
            }) : null);
        }
    };

    const formatPhone = (phone: string) => {
        phone = phone.replace(/\D/g, '');
        if (phone.length <= 10) {
            return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        } else {
            return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhone(e.target.value);
        setEditedPessoa(prevState => prevState ? ({
            ...prevState,
            telefone: formatted
        }) : null);
    };

    const handleSubmit = async () => {
        if (editedPessoa) {
            setIsSaving(true);
            try {
                const response = await api.patch(`/pessoas/${editedPessoa.id}`, editedPessoa);
                onUpdate(response.data);
                toast.success("Dados atualizados.", {
                    onClose: () => {
                        handleClose();
                    },
                });
            } catch (error) {
                console.error('Erro ao atualizar pessoa:', error);
                toast.error(BackendErrorMessage(error));
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleMudarParaCavu = async () => {
        if (editedPessoa && window.confirm('Deseja realmente mudar esta pessoa para Membro CAVU?')) {
            setIsSaving(true);
            try {
                const response = await api.patch(`/pessoas/${editedPessoa.id}`, {
                    ...editedPessoa,
                    isMembroCavu: true
                });
                onUpdate(response.data);
                toast.success("Pessoa alterada para Membro CAVU.", {
                    onClose: () => {
                        handleClose();
                    },
                });
            } catch (error) {
                console.error('Erro ao atualizar pessoa:', error);
                toast.error(BackendErrorMessage(error));
            } finally {
                setIsSaving(false);
            }
        }
    };

    const isFormValid = () => {
        return editedPessoa?.nome && editedPessoa?.cpf && editedPessoa?.vinculo?.nome;
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Editar Pessoa</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {editedPessoa ? (
                    <Form>
                        <Form.Group controlId="formNome" className="mb-3">
                            <Form.Label>Nome</Form.Label>
                            <Form.Control
                                type="text"
                                name="nome"
                                value={editedPessoa.nome}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group controlId="formCpf" className="mb-3">
                            <Form.Label>CPF</Form.Label>
                            <Form.Control
                                type="text"
                                name="cpf"
                                value={editedPessoa.cpf}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group controlId="formTelefone" className="mb-3">
                            <Form.Label>Telefone</Form.Label>
                            <Form.Control
                                type="text"
                                name="telefone"
                                value={editedPessoa.telefone || ''}
                                onChange={handlePhoneChange}
                                placeholder="(XX) XXXXX-XXXX"
                            />
                        </Form.Group>

                        <Form.Group controlId="formVinculo" className="mb-3">
                            <Form.Label>Vínculo</Form.Label>
                            <Form.Control
                                as="select"
                                name="vinculo"
                                value={editedPessoa.vinculo.nome}
                                onChange={handleChange}
                            >
                                {Object.values(VinculoNome).map(vinculo => (
                                    <option key={vinculo} value={vinculo}>
                                        {vinculo}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        <Form.Group controlId="formEmail" className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={editedPessoa.email || ''}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group controlId="formMatricula" className="mb-3">
                            <Form.Label>Matrícula</Form.Label>
                            <Form.Control
                                type="text"
                                name="matricula"
                                value={editedPessoa.matricula || ''}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Form>
                ) : (
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Carregando...</span>
                    </Spinner>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Button variant="warning" onClick={handleMudarParaCavu} disabled={isSaving}>
                        Mudar para Membro CAVU
                    </Button>
                    <div>
                        <Button variant="secondary" onClick={handleClose} disabled={isSaving} className="me-2">
                            Cancelar
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={!isFormValid() || isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                    />{' '}
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default PessoaEditModal;